import { botIdFromToken } from '@wui/im'
import linkifyHtml from 'linkify-html'
import { IntermediateRepresentation } from 'linkifyjs'

import { sanitizeOutboundMessage } from '@/shared/utils/sanitize'

export const BOT_CONTROL_COMMAND_TYPE = 'bot-command'
export const BOT_CONTROL_TOKEN_TYPE = 'bot-token'
export const BOT_CONTROL_ACTION_ATTR = 'data-bot-control-action'
export const BOT_CONTROL_PAYLOAD_ATTR = 'data-bot-control-payload'

const escapeAttr = (value: string) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
}

const isClockTimeToken = (value: string) => {
  return /^\d{1,4}:\d{1,4}$/.test(value)
}

export const toBotControlClipboardToken = (value: string) => {
  return value.startsWith('$') ? value.slice(1) : value
}

export const isBotControlCommandLink = (value: string) => {
  return /^\/[A-Za-z0-9]/.test(value)
}

export const isBotControlTokenLink = (value: string) => {
  const token = toBotControlClipboardToken(value)
  if (isClockTimeToken(token)) {
    return false
  }

  try {
    botIdFromToken(token)
    return true
  } catch {
    return false
  }
}

function convertTextToUrl (message: string, enableBotControlLinks = false) {
  const options = {
    defaultProtocol: 'https',
    attributes: {
      class: 'text-link',
    },
    rel: 'noopener noreferrer',
    target: '_blank',
    format: {
      url: (value: string) => decodeURI(value),
    },
    render: {
      url: ({ attributes, content }: IntermediateRepresentation) => {
        let attrs = ''
        for (const attr in attributes) {
          attrs += ` ${attr}="${attributes[attr]}"`
        }
        attrs += ` title="${content}"`
        return `<a${attrs}>${content}</a>`
      },
      [BOT_CONTROL_COMMAND_TYPE]: ({ content }: IntermediateRepresentation) => {
        return `<a href="#" class="text-link" ${BOT_CONTROL_ACTION_ATTR}="command" ${BOT_CONTROL_PAYLOAD_ATTR}="${escapeAttr(content)}">${content}</a>`
      },
      [BOT_CONTROL_TOKEN_TYPE]: ({ content }: IntermediateRepresentation) => {
        const payload = toBotControlClipboardToken(content)
        return `<a href="#" class="text-link" ${BOT_CONTROL_ACTION_ATTR}="copy" ${BOT_CONTROL_PAYLOAD_ATTR}="${escapeAttr(payload)}">${content}</a>`
      },
    },
    validate: (value: string, type: string) => {
      if (type === BOT_CONTROL_COMMAND_TYPE) {
        return enableBotControlLinks && isBotControlCommandLink(value)
      }
      if (type === BOT_CONTROL_TOKEN_TYPE) {
        return enableBotControlLinks && isBotControlTokenLink(value)
      }
      if (type !== 'url') return true

      try {
        // If URL doesn't have a protocol, prepend the default one
        const urlWithProtocol = value.match(/^https?:\/\//) ? value : `https://${value}`
        const url = new URL(urlWithProtocol)
        return !/\.(java|js|ts|cpp|py|php|go|sql|vue|tsx|jsx)$/.test(url.hostname)
      } catch {
        return false
      }
    },
  }
  return linkifyHtml(message, options)
}

const removeMailto = (message: string) => message.replace(/mailto:/g, '')

const hasInnerMarkup = (innerHtml: string) => /<[a-z][\s\S]*>/i.test(innerHtml)

const removeTagA = (message: string) => {
  return message.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (match, attrs: string, innerHtml: string) => {
    const href = attrs.match(/href\s*=\s*"([^"]*)"/i)?.[1] ?? ''
    const visible = innerHtml.replace(/<[^>]+>/g, '').trim()
    const hrefPlain = href.replace(/^mailto:/i, '')

    if (!visible) return hrefPlain

    // Formatted link content must stay inside <a>, otherwise markup is lost on unwrap
    if (hasInnerMarkup(innerHtml)) return match

    if (visible === href || visible === hrefPlain) return hrefPlain
    if (hrefPlain.includes(visible)) return visible

    return match
  })
}

const addSpacesBeforeUrls = (message: string) => {
  // Add space before URLs that are directly attached to text (e.g., "text123https://example.com")
  // Match: alphanumeric character followed by http:// or https://
  return message.replace(/([a-zA-Z0-9])(https?:\/\/)/g, '$1 $2')
}

const linkifyMessage = (message: string, enableBotControlLinks = false) => {
  try {
    // Drop clipboard theme colors (<span style="color…"> / <font>) before linkify.
    const withoutPresentation = sanitizeOutboundMessage(message)
    const clearTags = removeTagA(withoutPresentation)
    const clearMailto = removeMailto(clearTags)
    const withSpaces = addSpacesBeforeUrls(clearMailto)
    return convertTextToUrl(withSpaces, enableBotControlLinks).trim()
  } catch {
    return message
  }
}

export function createLinksFromText (message: string) {
  return linkifyMessage(message)
}

export function createBotControlLinksFromText (message: string) {
  return linkifyMessage(message, true)
}

export const getBotControlLinkFromEvent = (event: Event) => {
  const target = event.target
  if (!(target instanceof Element)) {
    return undefined
  }

  const link = target.closest(`a[${BOT_CONTROL_ACTION_ATTR}]`)
  if (!(link instanceof HTMLElement)) {
    return undefined
  }

  const action = link.getAttribute(BOT_CONTROL_ACTION_ATTR)
  const payload = link.getAttribute(BOT_CONTROL_PAYLOAD_ATTR)
  if ((action !== 'command' && action !== 'copy') || !payload) {
    return undefined
  }

  return { action, payload, link }
}
